import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { exportPresets, importPresets, pickPresetFile } from '@/services/preset-io';
import { ConfluenceSpaceCombobox } from './ConfluenceSpaceCombobox';
import * as audit from '@/services/audit';
import * as presetStore from '@/services/preset-store';
import {
  checkAllFreshness, preloadPresets,
  type FreshnessInfo, type PreloadProgress,
} from '@/services/preset-preload';
import type { Preset, SnapshotMeta, AnalysisJob, HydratedSnapshot, TimeRangeMode } from '@/services/preset-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faBoxArchive,
  faCalendarDays,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faCloudBolt,
  faCodeCommit,
  faDatabase,
  faDownload,
  faFire,
  faFolderOpen,
  faLayerGroup,
  faPenToSquare,
  faPlay,
  faPlus,
  faRotate,
  faSpinner,
  faSquare,
  faSquareCheck,
  faThumbtack,
  faThumbtackSlash,
  faTrash,
  faUpload,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import {
  timeRangeLabel, resolveTimeRange,
  ROLLING_PRESETS, CALENDAR_PRESETS,
} from '@/services/preset-types';

// ==================== Types ====================

interface AvailableRepo {
  id: string;
  name: string;
  type: 'local' | 'bitbucket' | 'github' | 'gitlab';
  url?: string;
  path?: string;
  description?: string;
  project?: string;
  updatedOn?: string;
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface ConfluenceSpace {
  key: string;
  name: string;
  type?: 'global' | 'personal' | 'knowledge_base' | string;
}

interface PresetGridProps {
  availableRepos: AvailableRepo[];
  jiraProjects: JiraProject[];
  hasJira: boolean;
  confluenceSpaces?: ConfluenceSpace[];
  hasConfluence?: boolean;
  isDark: boolean;
  onOpenPreset: (preset: Preset, snapshot: HydratedSnapshot | null) => void;
  onAnalyzePreset: (preset: Preset) => void;
  analysisJobs: Map<string, AnalysisJob>;
  onPresetsChanged?: () => void;
}

// ==================== Preset Edit Modal ====================

interface PresetModalProps {
  preset?: Preset;
  availableRepos: AvailableRepo[];
  jiraProjects: JiraProject[];
  hasJira: boolean;
  confluenceSpaces?: ConfluenceSpace[];
  hasConfluence?: boolean;
  isDark: boolean;
  onSave: (preset: Preset) => void;
  onCancel: () => void;
}

function PresetModal({ preset, availableRepos, jiraProjects, hasJira, confluenceSpaces = [], hasConfluence, isDark, onSave, onCancel }: PresetModalProps) {
  const [name, setName] = useState(preset?.name || '');
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set(preset?.repoIds || []));
  const [jiraProject, setJiraProject] = useState(preset?.jiraProjectKey || '');
  const [confluenceSpace, setConfluenceSpace] = useState(preset?.confluenceSpaceKey || '');
  const [timeRange, setTimeRange] = useState<TimeRangeMode>(preset?.timeRange || { type: 'rolling', hours: 30 * 24, label: 'Last 30 days' });
  const [timeRangeTab, setTimeRangeTab] = useState<'rolling' | 'calendar' | 'custom'>(preset?.timeRange?.type || 'rolling');
  const [customFrom, setCustomFrom] = useState(preset?.timeRange?.type === 'custom' ? preset.timeRange.from : '');
  const [customTo, setCustomTo] = useState(preset?.timeRange?.type === 'custom' ? preset.timeRange.to : '');
  const [repoSearch, setRepoSearch] = useState('');

  const filteredRepos = useMemo(() => {
    if (!repoSearch) return availableRepos;
    const q = repoSearch.toLowerCase();
    return availableRepos.filter(r => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
  }, [availableRepos, repoSearch]);

  const handleSave = async () => {
    if (!name.trim()) return;
    const now = new Date().toISOString();
    const sortOrder = preset?.sortOrder ?? await presetStore.getNextSortOrder();
    const finalTimeRange: TimeRangeMode = timeRangeTab === 'custom'
      ? { type: 'custom', from: customFrom, to: customTo }
      : timeRange;

    onSave({
      id: preset?.id || crypto.randomUUID(),
      name: name.trim(),
      createdAt: preset?.createdAt || now,
      updatedAt: now,
      repoIds: Array.from(selectedRepos),
      jiraProjectKey: jiraProject || undefined,
      confluenceSpaceKey: confluenceSpace || undefined,
      timeRange: finalTimeRange,
      pinned: preset?.pinned || false,
      sortOrder,
    });
  };

  const toggleRepo = (id: string) => {
    setSelectedRepos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onCancel}>
      <div
        className={`w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl shadow-2xl border ${isDark ? 'bg-slate-900 border-slate-700/50' : 'bg-white border-slate-200'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {preset ? 'Edit Preset' : 'New Preset'}
          </h2>
          <button onClick={onCancel} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
            <FontAwesomeIcon icon={faXmark} className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Name */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Preset Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Sprint 42 Review, Q1 Overview..."
              autoFocus
              className={`w-full px-3 py-2 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'}`}
            />
          </div>

          {/* Repositories */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Repositories ({selectedRepos.size} selected)
            </label>
            {availableRepos.length > 6 && (
              <input
                type="text"
                value={repoSearch}
                onChange={e => setRepoSearch(e.target.value)}
                placeholder="Search repos..."
                className={`w-full px-3 py-1.5 text-xs rounded-lg border mb-2 outline-none focus:ring-1 focus:ring-blue-500 ${isDark ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'}`}
              />
            )}
            <div className="flex gap-1.5 mb-2">
              <button onClick={() => setSelectedRepos(new Set(availableRepos.map(r => r.id)))}
                className={`text-[10px] px-2 py-1 rounded ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-500'}`}>Select All</button>
              <button onClick={() => setSelectedRepos(new Set())}
                className={`text-[10px] px-2 py-1 rounded ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-500'}`}>None</button>
            </div>
            <div className={`max-h-40 overflow-y-auto rounded-lg border p-1.5 space-y-0.5 ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}>
              {filteredRepos.map(repo => (
                <button
                  key={repo.id}
                  onClick={() => toggleRepo(repo.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-xs transition-colors ${
                    selectedRepos.has(repo.id)
                      ? (isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-700')
                      : (isDark ? 'text-slate-400 hover:bg-slate-700/50' : 'text-slate-600 hover:bg-slate-100')
                  }`}
                >
                  <div className={`w-3 h-3 rounded-sm border flex items-center justify-center ${
                    selectedRepos.has(repo.id)
                      ? 'bg-blue-500 border-blue-500'
                      : (isDark ? 'border-slate-600' : 'border-slate-300')
                  }`}>
                    {selectedRepos.has(repo.id) && <FontAwesomeIcon icon={faCircleCheck} className="h-2.5 w-2.5 text-white" />}
                  </div>
                  <span className="truncate flex-1">{repo.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-500' : 'bg-slate-200 text-slate-400'}`}>{repo.type}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Jira Project */}
          {hasJira && (
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Jira Project (optional)</label>
              <select
                value={jiraProject}
                onChange={e => setJiraProject(e.target.value)}
                className={`w-full px-3 py-2 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              >
                <option value="">None</option>
                {jiraProjects.map(p => <option key={p.key} value={p.key}>{p.name} ({p.key})</option>)}
              </select>
            </div>
          )}

          {/* Confluence Space */}
          {hasConfluence && (
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Confluence Space (optional)</label>
              <ConfluenceSpaceCombobox
                value={confluenceSpace}
                onChange={setConfluenceSpace}
                spaces={confluenceSpaces}
                isDark={isDark}
                showAllOption
              />
            </div>
          )}

          {/* Time Range */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Time Range</label>
            <div className={`flex gap-1 p-0.5 rounded-lg mb-3 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
              {(['rolling', 'calendar', 'custom'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setTimeRangeTab(tab)}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize ${
                    timeRangeTab === tab
                      ? (isDark ? 'bg-slate-700 text-white shadow' : 'bg-white text-slate-900 shadow')
                      : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {timeRangeTab === 'rolling' && (
              <div className="flex flex-wrap gap-1.5">
                {ROLLING_PRESETS.map(rp => {
                  const isActive = timeRange.type === 'rolling' && (timeRange as any).hours === (rp as any).hours;
                  return (
                    <button
                      key={(rp as any).hours}
                      onClick={() => setTimeRange(rp)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        isActive
                          ? (isDark ? 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/30' : 'bg-cyan-100 text-cyan-700 ring-1 ring-cyan-300')
                          : (isDark ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
                      }`}
                    >
                      {timeRangeLabel(rp)}
                    </button>
                  );
                })}
              </div>
            )}

            {timeRangeTab === 'calendar' && (
              <div className="flex flex-wrap gap-1.5">
                {CALENDAR_PRESETS.map(cp => {
                  const isActive = timeRange.type === 'calendar' && (timeRange as any).preset === (cp as any).preset;
                  return (
                    <button
                      key={(cp as any).preset}
                      onClick={() => setTimeRange(cp)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        isActive
                          ? (isDark ? 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/30' : 'bg-cyan-100 text-cyan-700 ring-1 ring-cyan-300')
                          : (isDark ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
                      }`}
                    >
                      {timeRangeLabel(cp)}
                    </button>
                  );
                })}
              </div>
            )}

            {timeRangeTab === 'custom' && (
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border outline-none ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                />
                <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>to</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border outline-none ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                />
              </div>
            )}

            {/* Show resolved range */}
            <div className={`mt-2 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {(() => {
                const resolved = resolveTimeRange(timeRangeTab === 'custom' ? { type: 'custom', from: customFrom || new Date().toISOString(), to: customTo || new Date().toISOString() } : timeRange);
                return `${resolved.from.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} — ${resolved.to.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`;
              })()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-6 py-4 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
          <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {selectedRepos.size} repo{selectedRepos.size !== 1 ? 's' : ''}
            {jiraProject && <> · {jiraProject}</>}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim() || (selectedRepos.size === 0 && !jiraProject)}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-all ${
                !name.trim() || (selectedRepos.size === 0 && !jiraProject)
                  ? (isDark ? 'bg-slate-800 text-slate-600 cursor-not-allowed' : 'bg-slate-100 text-slate-400 cursor-not-allowed')
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/25'
              }`}
            >
              {preset ? 'Save Changes' : 'Create Preset'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Freshness Badge ====================

function FreshnessBadge({ freshness, isHot, isDark }: { freshness?: FreshnessInfo; isHot: boolean; isDark: boolean }) {
  if (!freshness || freshness.status === 'none') {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>
        <FontAwesomeIcon icon={faDatabase} className="h-2.5 w-2.5" /> No snapshot
      </span>
    );
  }

  const hotIndicator = isHot ? (
    <span className="flex-shrink-0"><FontAwesomeIcon icon={faFire} className="h-2.5 w-2.5 text-orange-400" /></span>
  ) : null;

  if (freshness.status === 'fresh') {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
        {hotIndicator}
        <FontAwesomeIcon icon={faCircleCheck} className="h-2.5 w-2.5" /> Fresh
      </span>
    );
  }
  if (freshness.status === 'stale') {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
        {hotIndicator}
        <FontAwesomeIcon icon={faClock} className="h-2.5 w-2.5" /> Stale · {freshness.label}
      </span>
    );
  }
  if (freshness.status === 'expired') {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-100 text-red-700'}`}>
        <FontAwesomeIcon icon={faCircleExclamation} className="h-2.5 w-2.5" /> Expired · {freshness.label}
      </span>
    );
  }
  return null;
}

// ==================== Job Progress ====================

function JobProgress({ job, isDark }: { job: AnalysisJob; isDark: boolean }) {
  if (job.status === 'queued') {
    return (
      <div className={`flex items-center gap-2 text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        <FontAwesomeIcon icon={faClock} className="h-3 w-3" /> Queued...
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-1.5 text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-blue-400" />
          <span className="truncate max-w-[200px]">{job.message || 'Analyzing...'}</span>
        </div>
        <span className={`text-[10px] font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{job.progress}%</span>
      </div>
      <div className={`w-full h-1 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
        <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300" style={{ width: `${job.progress}%` }} />
      </div>
    </div>
  );
}

// ==================== Main Preset Grid ====================

export function PresetGrid({
  availableRepos, jiraProjects, hasJira, confluenceSpaces = [], hasConfluence, isDark,
  onOpenPreset, onAnalyzePreset, analysisJobs, onPresetsChanged,
}: PresetGridProps) {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [snapshotMetas, setSnapshotMetas] = useState<Map<string, SnapshotMeta>>(new Map());
  const [showModal, setShowModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState<Preset | undefined>();
  const [loading, setLoading] = useState(true);

  // Phase 2: Freshness, preload, hot cache
  const [freshnessMap, setFreshnessMap] = useState<Map<string, FreshnessInfo>>(new Map());
  const [hotCacheKeys, setHotCacheKeys] = useState<Set<string>>(new Set());
  const [selectedForPreload, setSelectedForPreload] = useState<Set<string>>(new Set());
  const [preloading, setPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState<PreloadProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Load presets + snapshot metas + freshness on mount
  const refresh = useCallback(async () => {
    try {
      const [allPresets, allMetas] = await Promise.all([
        presetStore.getAllPresets(),
        presetStore.getAllSnapshotMetas(),
      ]);
      setPresets(allPresets);

      // Build map: presetId → latest meta
      const metaMap = new Map<string, SnapshotMeta>();
      for (const m of allMetas) {
        const existing = metaMap.get(m.presetId);
        if (!existing || new Date(m.analysedAt) > new Date(existing.analysedAt)) {
          metaMap.set(m.presetId, m);
        }
      }
      setSnapshotMetas(metaMap);

      // Check freshness for all presets
      const freshness = await checkAllFreshness(allPresets);
      setFreshnessMap(freshness);

      // Update hot cache keys
      setHotCacheKeys(new Set(presetStore.getHotCacheKeys()));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when jobs complete
  useEffect(() => {
    const doneJobs = Array.from(analysisJobs.values()).filter(j => j.status === 'done');
    if (doneJobs.length > 0) refresh();
  }, [analysisJobs, refresh]);

  // Toggle checkbox for preload selection
  const togglePreloadSelect = useCallback((presetId: string) => {
    setSelectedForPreload(prev => {
      const next = new Set(prev);
      if (next.has(presetId)) next.delete(presetId);
      else next.add(presetId);
      return next;
    });
  }, []);

  const selectAllForPreload = useCallback(() => {
    setSelectedForPreload(new Set(presets.map(p => p.id)));
  }, [presets]);

  const deselectAllForPreload = useCallback(() => {
    setSelectedForPreload(new Set());
  }, []);

  // Preload selected presets into hot cache
  const handlePreload = useCallback(async () => {
    const selected = presets.filter(p => selectedForPreload.has(p.id));
    if (selected.length === 0) return;

    setPreloading(true);
    abortRef.current = new AbortController();

    try {
      await preloadPresets(
        selected,
        async (preset) => {
          // Use the parent's analyze function — it saves snapshot + puts in hot cache
          await new Promise<void>((resolve, reject) => {
            onAnalyzePreset(preset);
            // Poll for job completion
            const check = setInterval(() => {
              const job = analysisJobs.get(preset.id);
              if (job?.status === 'done') { clearInterval(check); resolve(); }
              if (job?.status === 'failed') { clearInterval(check); reject(new Error(job.error || 'Analysis failed')); }
            }, 500);
            // Timeout after 5 minutes
            setTimeout(() => { clearInterval(check); reject(new Error('Timeout')); }, 300000);
          });
        },
        (progress) => setPreloadProgress(progress),
        undefined,
        abortRef.current.signal
      );
    } finally {
      setPreloading(false);
      setPreloadProgress(null);
      setSelectedForPreload(new Set());
      abortRef.current = null;
      await refresh();
    }
  }, [presets, selectedForPreload, onAnalyzePreset, analysisJobs, refresh]);

  const cancelPreload = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleSavePreset = useCallback(async (preset: Preset) => {
    await presetStore.savePreset(preset);
    audit.auditPresetCreated(preset.name, { repoCount: preset.repoIds.length, jiraProject: preset.jiraProjectKey });
    setShowModal(false);
    setEditingPreset(undefined);
    await refresh();
    onPresetsChanged?.();
  }, [refresh, onPresetsChanged]);

  const handleDeletePreset = useCallback(async (id: string) => {
    if (!confirm('Delete this preset and its snapshots?')) return;
    const p = presets.find(pr => pr.id === id);
    audit.auditPresetDeleted(p?.name || id);
    await presetStore.deletePreset(id);
    await refresh();
    onPresetsChanged?.();
  }, [refresh, onPresetsChanged]);

  const handleTogglePin = useCallback(async (preset: Preset) => {
    preset.pinned = !preset.pinned;
    await presetStore.savePreset(preset);
    await refresh();
    onPresetsChanged?.();
  }, [refresh, onPresetsChanged]);

  const handleOpenCached = useCallback(async (preset: Preset) => {
    const snapshot = await presetStore.loadSnapshot(preset.id);
    onOpenPreset(preset, snapshot);
  }, [onOpenPreset]);

  if (loading) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
        <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <FontAwesomeIcon icon={faBolt} className="h-5 w-5 text-amber-400" />
              Analysis Presets
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Save filter configurations for instant switching during meetings
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Preload button */}
            {presets.length > 0 && !preloading && selectedForPreload.size > 0 && (
              <button
                onClick={handlePreload}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-lg shadow-orange-500/25 transition-all"
              >
                <FontAwesomeIcon icon={faCloudBolt} className="h-4 w-4" /> Preload {selectedForPreload.size}
              </button>
            )}
            {preloading && (
              <button
                onClick={cancelPreload}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
              >
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4" /> Cancel
              </button>
            )}
            {presets.length > 0 && (
              <button
                onClick={() => { exportPresets(); audit.auditPresetExported(presets.length); }}
                title="Export presets"
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800 text-slate-500 hover:text-slate-300' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}
              >
                <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={async () => {
                const file = await pickPresetFile();
                if (!file) return;
                const result = await importPresets(file);
                audit.auditPresetImported(result.imported, result.skipped);
                if (result.imported > 0) {
                  await refresh();
                  onPresetsChanged?.();
                }
                if (result.errors.length > 0) {
                  alert(`Import errors:\n${result.errors.join('\n')}`);
                } else {
                  alert(`Imported ${result.imported} preset(s), skipped ${result.skipped} duplicate(s).`);
                }
              }}
              title="Import presets"
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800 text-slate-500 hover:text-slate-300' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}
            >
              <FontAwesomeIcon icon={faUpload} className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setEditingPreset(undefined); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/25 transition-all"
            >
              <FontAwesomeIcon icon={faPlus} className="h-4 w-4" /> New Preset
            </button>
          </div>
        </div>

        {/* Preload progress bar */}
        {preloading && preloadProgress && (
          <div className={`mb-4 p-3 rounded-lg border ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Preloading {preloadProgress.completed}/{preloadProgress.total} presets...
              </span>
              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {Math.round((preloadProgress.completed / preloadProgress.total) * 100)}%
              </span>
            </div>
            <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
              <div
                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-300"
                style={{ width: `${(preloadProgress.completed / preloadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Empty state */}
        {presets.length === 0 && (
          <div className={`flex-1 flex items-center justify-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            <div className="text-center max-w-md">
              <FontAwesomeIcon icon={faBoxArchive} className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className={`text-lg font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No presets yet</p>
              <p className="text-sm mb-6">Create a preset to save your repo selection, time range, and filters. Presets let you switch between views instantly during meetings.</p>
              <button
                onClick={() => { setEditingPreset(undefined); setShowModal(true); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/25 transition-all"
              >
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4" /> Create Your First Preset
              </button>
            </div>
          </div>
        )}

        {/* Preset table */}
        {presets.length > 0 && (
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'bg-slate-800/50' : 'bg-slate-50'}>
                  <th className="w-8 px-2 py-2.5">
                    <button
                      onClick={selectedForPreload.size === presets.length ? deselectAllForPreload : selectAllForPreload}
                      className={`p-0.5 rounded transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                    >
                      {selectedForPreload.size === presets.length
                        ? <FontAwesomeIcon icon={faSquareCheck} className={`h-3.5 w-3.5 ${isDark ? 'text-orange-400' : 'text-orange-500'}`} />
                        : <FontAwesomeIcon icon={faSquare} className={`h-3.5 w-3.5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                      }
                    </button>
                  </th>
                  <th className={`text-left text-[10px] font-semibold uppercase tracking-wider px-4 py-2.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Preset</th>
                  <th className={`text-left text-[10px] font-semibold uppercase tracking-wider px-4 py-2.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Sources</th>
                  <th className={`text-left text-[10px] font-semibold uppercase tracking-wider px-4 py-2.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Time Range</th>
                  <th className={`text-left text-[10px] font-semibold uppercase tracking-wider px-4 py-2.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Last Analysis</th>
                  <th className={`text-left text-[10px] font-semibold uppercase tracking-wider px-4 py-2.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Status</th>
                  <th className={`text-right text-[10px] font-semibold uppercase tracking-wider px-4 py-2.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {presets.map(preset => {
                  const meta = snapshotMetas.get(preset.id);
                  const job = analysisJobs.get(preset.id);
                  const isAnalyzing = job && (job.status === 'running' || job.status === 'queued');
                  const freshness = freshnessMap.get(preset.id);
                  const isHot = hotCacheKeys.has(preset.id);
                  const isSelected = selectedForPreload.has(preset.id);
                  const repoNames = preset.repoIds
                    .map(id => availableRepos.find(r => r.id === id)?.name)
                    .filter(Boolean);

                  return (
                    <tr key={preset.id} className={`border-t transition-colors ${isDark ? 'border-slate-700/30 hover:bg-slate-800/30' : 'border-slate-100 hover:bg-slate-50'}`}>
                      {/* Checkbox */}
                      <td className="w-8 px-2 py-3">
                        <button
                          onClick={() => togglePreloadSelect(preset.id)}
                          className={`p-0.5 rounded transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                        >
                          {isSelected
                            ? <FontAwesomeIcon icon={faSquareCheck} className={`h-3.5 w-3.5 ${isDark ? 'text-orange-400' : 'text-orange-500'}`} />
                            : <FontAwesomeIcon icon={faSquare} className={`h-3.5 w-3.5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                          }
                        </button>
                      </td>
                      {/* Name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {preset.pinned && <FontAwesomeIcon icon={faThumbtack} className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{preset.name}</span>
                        </div>
                      </td>

                      {/* Sources */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {repoNames.length > 0 && (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-orange-500/10 text-orange-400' : 'bg-orange-50 text-orange-600'}`} title={repoNames.join(', ')}>
                              <FontAwesomeIcon icon={faCodeCommit} className="h-2.5 w-2.5" /> {repoNames.length}
                            </span>
                          )}
                          {preset.jiraProjectKey && (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                              <FontAwesomeIcon icon={faLayerGroup} className="h-2.5 w-2.5" /> {preset.jiraProjectKey}
                            </span>
                          )}
                          {preset.confluenceSpaceKey && (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-sky-500/10 text-sky-400' : 'bg-sky-50 text-sky-600'}`}>
                              <FontAwesomeIcon icon={faDatabase} className="h-2.5 w-2.5" /> {preset.confluenceSpaceKey === '__ALL__' ? 'All Spaces' : preset.confluenceSpaceKey}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Time Range */}
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          <FontAwesomeIcon icon={faCalendarDays} className="h-3 w-3 flex-shrink-0" />
                          {timeRangeLabel(preset.timeRange)}
                        </span>
                      </td>

                      {/* Last Analysis */}
                      <td className="px-4 py-3">
                        {isAnalyzing ? (
                          <div className="w-48"><JobProgress job={job!} isDark={isDark} /></div>
                        ) : meta ? (
                          <div>
                            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                              {new Date(meta.analysedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <div className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                              {meta.commitCount} commits · {meta.jiraEventCount} events{meta.confluenceEventCount ? ` · ${meta.confluenceEventCount} pages` : ''} · {(meta.compressedSize / 1024).toFixed(0)}KB
                            </div>
                          </div>
                        ) : (
                          <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Never</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        {isAnalyzing ? (
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                            <FontAwesomeIcon icon={faSpinner} className="h-2.5 w-2.5 animate-spin" /> Analyzing
                          </span>
                        ) : (
                          <FreshnessBadge freshness={freshness} isHot={isHot} isDark={isDark} />
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {meta && meta.status === 'ready' && (
                            <button
                              onClick={() => handleOpenCached(preset)}
                              title="Open cached analysis"
                              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-emerald-500/20 text-emerald-400' : 'hover:bg-emerald-50 text-emerald-600'}`}
                            >
                              <FontAwesomeIcon icon={faFolderOpen} className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => onAnalyzePreset(preset)}
                            disabled={!!isAnalyzing}
                            title={meta ? 'Re-run analysis' : 'Run analysis'}
                            className={`p-1.5 rounded-lg transition-colors ${isAnalyzing ? 'opacity-30 cursor-not-allowed' : (isDark ? 'hover:bg-blue-500/20 text-blue-400' : 'hover:bg-blue-50 text-blue-600')}`}
                          >
                            {meta ? <FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" /> : <FontAwesomeIcon icon={faPlay} className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            onClick={() => handleTogglePin(preset)}
                            title={preset.pinned ? 'Unpin' : 'Pin'}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-amber-500/20 text-slate-500 hover:text-amber-400' : 'hover:bg-amber-50 text-slate-400 hover:text-amber-600'}`}
                          >
                            {preset.pinned ? <FontAwesomeIcon icon={faThumbtackSlash} className="h-3.5 w-3.5" /> : <FontAwesomeIcon icon={faThumbtack} className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            onClick={() => { setEditingPreset(preset); setShowModal(true); }}
                            title="Edit preset"
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeletePreset(preset.id)}
                            title="Delete preset"
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-red-500/20 text-slate-500 hover:text-red-400' : 'hover:bg-red-50 text-slate-400 hover:text-red-500'}`}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <PresetModal
          preset={editingPreset}
          availableRepos={availableRepos}
          jiraProjects={jiraProjects}
          hasJira={hasJira}
          confluenceSpaces={confluenceSpaces}
          hasConfluence={hasConfluence}
          isDark={isDark}
          onSave={handleSavePreset}
          onCancel={() => { setShowModal(false); setEditingPreset(undefined); }}
        />
      )}
    </>
  );
}
